import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ContentTranslation, TranslatableEntityType } from "@/i18n";

/**
 * Hooks for the editor translation-review surface (fas 2.3b).
 *
 * See `docs/SoT/localization.md` §5.4 — Alt C design:
 *   - drafts are auto-generated on save by `suggestContentTranslations`
 *   - this hook surfaces them so the editor can approve/edit/reject
 *   - status flips to 'approved' only on explicit user action
 */

const pendingKey = (entityType: TranslatableEntityType, entityId: string | null | undefined) =>
  ["content_translations", "pending", entityType, entityId ?? null] as const;

/**
 * Fetches all translations (draft, stale, AND approved) for an entity so
 * the editor review surface can both review new suggestions and re-edit
 * already-published English text. Returns an empty array if `entityId` is
 * not yet known. RLS guards the rows — the request naturally returns 0
 * rows for users without write access.
 *
 * Consumers that only care about the unreviewed count (e.g. badge label)
 * should filter `rows.filter(r => r.status !== 'approved').length`.
 */
export function usePendingTranslations(
  entityType: TranslatableEntityType,
  entityId: string | null | undefined,
) {
  return useQuery<ContentTranslation[]>({
    queryKey: pendingKey(entityType, entityId),
    enabled: Boolean(entityId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!entityId) return [];
      const { data, error } = await supabase
        .from("content_translations")
        .select(
          "id, entity_type, entity_id, field_name, locale, source_locale, source_hash, value, status, generated_by_ai, created_at, updated_at, updated_by",
        )
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .in("status", ["draft", "stale", "approved"])
        .order("status")
        .order("field_name");
      if (error) throw error;
      return (data ?? []) as ContentTranslation[];
    },
  });
}

interface ApproveArgs {
  id: string;
  /** Optional override — if the editor edits before approving. */
  value?: string;
}

/**
 * Mutations for the review surface: approve (with optional edit), reject.
 * All invalidate the pending queue so the badge count updates immediately.
 */
export function useTranslationMutations(
  entityType: TranslatableEntityType,
  entityId: string | null | undefined,
) {
  const qc = useQueryClient();
  const key = pendingKey(entityType, entityId);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key });
    // Also invalidate the public-render query so visitors see the new EN
    // immediately if they have the page open.
    qc.invalidateQueries({ queryKey: ["content_translations", entityType, entityId] });
  };

  const approve = useMutation({
    mutationFn: async ({ id, value }: ApproveArgs) => {
      const patch: Record<string, unknown> = { status: "approved" };
      if (typeof value === "string") patch.value = value;
      const { error } = await supabase
        .from("content_translations")
        .update(patch)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: async (id: string) => {
      // We don't actually DELETE — the SoT keeps the unique constraint
      // intact. Setting status='draft' with an empty value is reserved for
      // the "edit"-flow. For "reject" we fall back to delete because the
      // host's intent is "throw this suggestion away". DELETE is service-
      // role-only by RLS, so this will fail for non-admins. In that case
      // we degrade to "blank the draft" so the badge clears.
      const { error: delErr } = await supabase.from("content_translations").delete().eq("id", id);
      if (!delErr) return;
      // Fallback: blank value so the row is no longer a useful suggestion.
      // The next save-driven auto-suggest will replace it.
      const { error: upErr } = await supabase
        .from("content_translations")
        .update({ value: "" })
        .eq("id", id);
      if (upErr) throw upErr;
    },
    onSuccess: invalidate,
  });

  return { approve, reject };
}
